import { FC, PropsWithChildren, createContext, useContext } from 'react';

import noop from 'utils/noop';
import { AnyComparator, CastType, FieldType } from '../types';
import { useCastTypeState } from '../hooks/condition/useCastTypeState';
import {
  useComparatorState,
  useFieldState,
  useFieldTypeState,
  useValueState,
  useValuesState,
  ConditionValue,
} from '../hooks/condition';

type ConditionStateContext = {
  castType?: CastType;
  comparator?: AnyComparator;
  field?: string;
  fieldType?: FieldType;
  values?: string[];
  value?: ConditionValue;
  setCastType: (newCastType?: CastType) => void;
  setComparator: (newComparator?: AnyComparator) => void;
  setField: (newField?: string) => void;
  setFieldType: (newFieldType?: FieldType) => void;
  setValues: (values?: string[]) => void;
  setValue: (value?: ConditionValue) => void;
};

export const conditionDefaultState: ConditionStateContext = {
  setCastType: noop,
  setComparator: noop,
  setField: noop,
  setFieldType: noop,
  setValues: noop,
  setValue: noop,
};

const ConditionStateContext = createContext(conditionDefaultState);

export const useConditionStateContext = () => useContext(ConditionStateContext);

export const ConditionStateContextProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { castType, setCastType } = useCastTypeState();
  const { comparator, setComparator } = useComparatorState();
  const { field, setField } = useFieldState();
  const { fieldType, setFieldType } = useFieldTypeState();
  const { values, setValues } = useValuesState();
  const { value, setValue } = useValueState();

  return (
    <ConditionStateContext.Provider
      value={{
        castType,
        comparator,
        field,
        fieldType,
        values,
        value,
        setCastType,
        setComparator,
        setField,
        setFieldType,
        setValues,
        setValue,
      }}
    >
      {children}
    </ConditionStateContext.Provider>
  );
};
